import { ComponentType, ReactNode, Suspense, lazy } from 'react'
import { Navigate } from 'react-router-dom'
import { RouteObject } from 'react-router'

import SidebarLayout from '../pages/admins/layouts/SidebarLayout'
import BaseLayout from '../pages/admins/layouts/BaseLayout'
import AuthTheme from '../pages/auth/authBrand'

import SuspenseLoader from '../components/SuspenseLoader'
import { elearningRoutes } from './elearningRoutes'

const Loader =
  <P extends object>(Component: ComponentType<P>) =>
  (props: P): ReactNode =>
    (
      <Suspense fallback={<SuspenseLoader />}>
        <Component {...props} />
      </Suspense>
    )

// Pages

const Overview = Loader(lazy(() => import('../pages/admins/content/overview')))

// Applications

const Helpdesk = Loader(
  lazy(() => import('../pages/admins/content/applications/Helpdesk'))
)

const UserProfile = Loader(
  lazy(() => import('../pages/admins/content/applications/Users/profile'))
)
const UserSettings = Loader(
  lazy(() => import('../pages/admins/content/applications/Users/settings'))
)
const Clients = Loader(
  lazy(() => import('../pages/admins/content/applications/Clients'))
)
const ClientDetail = Loader(
  lazy(() => import('../pages/admins/content/applications/Clients/detail'))
)
// management
const ManagementUploadData = Loader(
  lazy(() => import('../pages/admins/content/management/UploadData'))
)
const ManagementUsers = Loader(
  lazy(() => import('../pages/admins/content/management/Users'))
)
const ManagementInvite = Loader(
  lazy(() => import('../pages/admins/content/management/Invite'))
)
const ManagementOrganisaties = Loader(
  lazy(() => import('../pages/admins/content/management/Organisaties'))
)

const ManagementNotificaties = Loader(
  lazy(() => import('../pages/admins/content/management/Notificaties'))
)
// CHL-146-008: the "Quote van de dag" list.
const ManagementQuotes = Loader(
  lazy(() => import('../pages/admins/content/management/Quotes'))
)
// CHL-073-009: per-task database access, approved by Bloom. Superbeheerder
// only — this router is the superbeheerder's, routesAdmin does not get it.
const ManagementDatabaseToegang = Loader(
  lazy(() => import('../pages/admins/content/management/DatabaseToegang'))
)

// Status
// const Status404 = Loader(
//   lazy(() => import('../pages/admins/content/pages/Status/Status404'))
// )

// Auth
const AuthVerifyAccount = Loader(
  lazy(() => import('../pages/auth/verifyAccount'))
)
const AuthOtpCode = Loader(lazy(() => import('../pages/auth/otpCode')))
const AuthForgotPassword = Loader(
  lazy(() => import('../pages/auth/forgotPassword'))
)
const ResetPassword = Loader(lazy(() => import('../pages/auth/resetPassword')))
const Invitation = Loader(lazy(() => import('../pages/auth/invitation')))

const routesDev: RouteObject[] = [
  {
    path: '',
    element: <BaseLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="/dashboards" replace />,
      },
      {
        path: '/auth',
        element: <AuthTheme />,
        children: [
          {
            path: '',
            element: <Navigate to="login" replace />,
          },
          {
            path: 'forgot-password',
            element: <AuthForgotPassword />,
          },
          {
            path: 'verify-email/:token',
            element: <AuthVerifyAccount />,
          },
          {
            path: 'otp-code',
            element: <AuthOtpCode />,
          },
          {
            path: 'reset-password',
            element: <ResetPassword />,
          },
          {
            path: 'invitation/:token',
            element: <Invitation />,
          },
        ],
      },
      {
        path: '/dashboards',
        element: <SidebarLayout />,
        children: [
          {
            // FB-8: the client surface (chat-ai / kalender / psychologie /
            // feeling-chart) is retired on the web, so dev lands on the
            // clients overview like every other staff role.
            path: '',
            element: <Navigate to="clienten" replace />,
          },
          {
            path: 'helpdesk',
            element: <Helpdesk />,
          },
          {
            // Redirect stale chat-ai bookmarks/links to the helpdesk.
            path: 'chat-ai',
            element: <Navigate to="/dashboards/helpdesk" replace />,
          },
          // Behandelaar client oversight (Module 9). Dev holds supervisor
          // scope, so the list collapses to all clients.
          {
            path: 'clienten',
            element: <Clients />,
          },
          {
            path: 'clienten/:id',
            element: <ClientDetail />,
          },
          {
            // Sidebar "Instellingen" links to /dashboards/profile/settings,
            // so the profile tree is mounted under /dashboards and the link
            // doesn't fall through to the wildcard redirect.
            path: 'profile',
            children: [
              {
                path: '',
                element: <Navigate to="details" replace />,
              },
              {
                path: 'details',
                element: <UserProfile />,
              },
              {
                path: 'settings',
                element: <UserSettings />,
              },
            ],
          },
        ],
      },
      {
        path: 'management',
        element: <SidebarLayout />,
        children: [
          {
            path: '',
            element: <Navigate to="upload-data" replace />,
          },
          {
            // CHL-058-009: no chatbot in the superbeheerder console — the bot
            // trainer and "Bot instellingen" (setting) stay beheerder-only.
            path: 'bot-trainer',
            element: <Navigate to="/management" replace />,
          },
          {
            path: 'upload-data',
            element: <ManagementUploadData />,
          },
          {
            path: 'setting',
            element: <Navigate to="/management" replace />,
          },
          {
            path: 'users',
            element: <ManagementUsers />,
          },
          {
            path: 'invite',
            element: <ManagementInvite />,
          },
          {
            path: 'organisaties',
            element: <ManagementOrganisaties />,
          },
          {
            path: 'notificaties',
            element: <ManagementNotificaties />,
          },
          {
            path: 'quotes',
            element: <ManagementQuotes />,
          },
          {
            path: 'databasetoegang',
            element: <ManagementDatabaseToegang />,
          },
          {
            // CHL-057-009: the questionnaire menu is gone; old links land on
            // the management start page instead of a dead screen.
            path: 'kuesioner/*',
            element: <Navigate to="/management" replace />,
          },
          ...elearningRoutes,
          {
            path: 'profile',
            children: [
              {
                path: '',
                element: <Navigate to="details" replace />,
              },
              {
                path: 'details',
                element: <UserProfile />,
              },
              {
                path: 'settings',
                element: <UserSettings />,
              },
            ],
          },
        ],
      },
      {
        path: 'overview',
        element: <Overview />,
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
]

export default routesDev
